import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-button';
import './vitesse-schema-form';
import '@vitesse/base-ui/elements/inputs/vitesse-select';
import type { BackendClient } from '../bridge/backend-client';
import type { PluginType } from '../../core/bridge/connect-client';

/**
 * <vitesse-add-controller-dialog> — the backend-driven Add Controller flow. NO
 * per-plugin UX is hardcoded here: it picks a plugin type (when more than one
 * accepts manual add), renders that type's `connection_form` via
 * <vitesse-schema-form>, and submits AddController. Validation is round-tripped —
 * field errors come back from the plugin (the authoritative validator) and render
 * inline; a general error renders as a banner. Emits `added` (controllerId) on
 * success, then closes.
 *
 *   <vitesse-add-controller-dialog .open=${open} .gatewayId=${gw}
 *       .backend=${backend} .types=${pluginTypes}
 *       @dialog-close=${() => (open = false)}
 *       @added=${(e) => …e.detail.controllerId}></vitesse-add-controller-dialog>
 */
@customElement('vitesse-add-controller-dialog')
export class VitesseAddControllerDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  @property() gatewayId = '';
  @property({ attribute: false }) backend?: BackendClient;
  @property({ attribute: false }) types: PluginType[] = [];

  @state() private pluginId = '';
  @state() private values: Record<string, string> = {};
  @state() private errors: Record<string, string> = {};
  @state() private generalError = '';
  @state() private busy = false;

  static styles = css`
    /* the type-picker form-group — matches the agent .form-group (label + field) */
    .form-group {
      margin-bottom: 16px;
    }
    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 500;
      color: var(--text-primary);
    }
    .general-err {
      margin: 0 0 12px;
      font-size: 13px;
      color: var(--red);
    }
    .empty {
      margin: 0;
      font-size: 13px;
      color: var(--text-secondary);
    }
  `;

  // Reset on open; auto-select the first addable type once the stream lands.
  willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open) this.reset();
    if (this.open && !this.selected() && this.addable().length > 0) {
      this.selectType(this.addable()[0].pluginId);
    }
  }

  private addable(): PluginType[] {
    return this.types.filter((t) => t.canManuallyAdd);
  }
  private selected(): PluginType | undefined {
    return this.types.find((t) => t.pluginId === this.pluginId);
  }

  // Selecting a type seeds the form with that type's declared field defaults, so a
  // submit always carries every field (not just the ones the user touched).
  private selectType(pluginId: string): void {
    this.pluginId = pluginId;
    const t = this.selected();
    this.values = t ? Object.fromEntries(t.connectionForm.map((f) => [f.key, f.default ?? ''])) : {};
    this.errors = {};
  }

  private reset(): void {
    this.generalError = '';
    this.busy = false;
    const a = this.addable();
    this.selectType(a.length > 0 ? a[0].pluginId : '');
  }

  private close(): void {
    this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }));
  }

  private async submit(): Promise<void> {
    const t = this.selected();
    if (!t || !this.backend || this.busy) return;
    this.busy = true;
    this.errors = {};
    this.generalError = '';
    try {
      const res = await this.backend.api.addController(this.gatewayId, t.pluginId, this.values);
      if (res.ok) {
        this.dispatchEvent(
          new CustomEvent('added', { detail: { controllerId: res.controllerId }, bubbles: true, composed: true }),
        );
        this.close();
        return;
      }
      const errs: Record<string, string> = {};
      for (const fe of res.fieldErrors) errs[fe.key] = fe.message;
      this.errors = errs;
      this.generalError = res.error || (res.fieldErrors.length === 0 ? 'Could not add controller.' : '');
    } catch (err) {
      this.generalError = err instanceof Error ? err.message : String(err);
    } finally {
      this.busy = false;
    }
  }

  render() {
    const t = this.selected();
    const addable = this.addable();
    return html`<vitesse-dialog .open=${this.open} heading="Add controller" @dialog-close=${this.close}>
      ${addable.length > 0
        ? html`<div class="form-group">
            <label>Controller type</label>
            <vitesse-select
              .options=${addable.map((x) => ({ value: x.pluginId, label: x.displayName }))}
              .value=${this.pluginId}
              @change=${(e: CustomEvent) => this.selectType(String((e.detail as { value: string }).value))}
            ></vitesse-select>
          </div>`
        : nothing}
      ${this.generalError ? html`<p class="general-err">${this.generalError}</p>` : nothing}
      ${t
        ? html`<vitesse-schema-form
            heading="Connection"
            .fields=${t.connectionForm}
            .values=${this.values}
            .errors=${this.errors}
            @form-change=${(e: CustomEvent) => (this.values = (e.detail as { values: Record<string, string> }).values)}
          ></vitesse-schema-form>`
        : html`<p class="empty">No plugin type supports manually adding a controller.</p>`}
      <vitesse-button slot="footer" color="red" @click=${this.close}>Cancel</vitesse-button>
      <vitesse-button slot="footer" color="blue" ?disabled=${this.busy || !t} @click=${this.submit}>
        ${this.busy ? 'Adding…' : 'Add'}
      </vitesse-button>
    </vitesse-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-add-controller-dialog': VitesseAddControllerDialog;
  }
}
